import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const realLevers = [
  {
    id: "admin",
    label: "Administrative & Operational Staff",
    pct: "10–14%",
    color: "#3b82f6",
    hook: "What you pay for the team that still can't keep up.",
    bullets: [
      {
        title: "The headcount cost",
        text: "Front desk, office manager, billing coordinator, scheduling — roughly $10K–$14K/mo in fully-loaded payroll on a $100K practice.",
      },
      {
        title: "Hiring & training drag",
        text: "Every new front-desk or biller takes $3K–$6K to recruit and 60–90 days to fully ramp. During that ramp they miscode claims, fumble calls, and drop tasks — the cost shows up as leakage, not as a line item.",
      },
      {
        title: "Churn & reliability",
        text: "Dental front-desk turnover runs 25–40% annually. PTO, sick days, and no-call no-shows mean the desk is understaffed multiple days a month — the phones, verifications, and follow-ups simply don't get done.",
      },
    ],
  },
  {
    id: "leakage",
    label: "Revenue Leakage",
    pct: "10–25%",
    color: "#6366f1",
    hook: "Money earned — but never collected.",
    bullets: [
      {
        title: "Missed inbound calls",
        text: "20–35% of business-hour calls go unanswered — 80% of them scheduling-related. Only 14% of new-patient callers leave a voicemail; the rest call the next practice. ≈ $5K–$15K/mo.",
      },
      {
        title: "RCM failures & denials",
        text: "15–20% of claims denied on first pass — 77% from admin errors (names, IDs, attachments). 67% are never resubmitted. ≈ $5K–$10K/mo in earned revenue that never hits the bank.",
      },
      {
        title: "Underpayments & aging AR",
        text: "No one reconciles paid claims vs. contracted rates. Secondary insurance left on the table. AR balloons past 90 days — tens of thousands per year quietly evaporate.",
      },
    ],
  },
  {
    id: "growth",
    label: "Growth",
    pct: "28–55%",
    color: "#a855f7",
    hook: "Revenue that could exist — but doesn't.",
    bullets: [
      {
        title: "Case acceptance",
        text: "Avg practice closes 35–50% of presented treatment; top performers hit 80–85%. A 15–20pt lift on $100K = $15K–$30K/mo — same patients already in the chair.",
      },
      {
        title: "Hygiene recall & retention",
        text: "55–65% recall rate means 35–45% of patients don't come back. Pushing to 80%+ = $50K–$100K/yr — plus downstream restorative from every diagnostic exam.",
      },
      {
        title: "Schedule optimization",
        text: "15–30% no-shows. No real-time waitlist fill. Manual scheduling creates gaps and conflicts that cut daily capacity 10–15%. ≈ $5K–$10K/mo in lost production.",
      },
      {
        title: "Missed growth potential",
        text: "When the office runs itself — clean ops, full schedule, collected AR — you get the bandwidth and peace of mind to actually grow: add a chair, open a second location, invest in marketing. Stuck in firefighting mode, that growth never happens.",
      },
    ],
  },
];

const RealOpportunitySlide = () => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1200px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-5 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]"
          >
            But the problem is{" "}
            <span className="text-blue-600">far bigger.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45 }}
            className="text-[19px] lg:text-[22px] text-slate-700 font-medium max-w-[1000px] mx-auto leading-[1.5]"
          >
            A $100K/month practice isn't really a $100K practice. It's a{" "}
            <span className="font-semibold text-slate-900">$150K–$190K practice</span>{" "}
            compressed by <span className="italic">operational gravity</span>.
          </motion.p>
        </div>

        {/* Bar visualization */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.1)] p-8"
        >
          <div className="relative">
            {/* Scale markers */}
            <div className="relative h-[18px] mb-1">
              {[0, 38, 95, 100, 190].map((v) => (
                <div
                  key={v}
                  className="absolute -translate-x-1/2 top-0 text-[11px] font-semibold text-slate-500 tabular-nums"
                  style={{ left: `${(v / 200) * 100}%` }}
                >
                  ${v}K
                </div>
              ))}
            </div>

            {/* Bar container */}
            <div className="relative h-[72px] w-full rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              {/* Take-home today: 0 → 38 */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(38 / 200) * 100}%` }}
                transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-blue-600 to-blue-500 flex items-center justify-center"
              >
                <div className="text-center text-white px-2">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] opacity-90">
                    Take-home today
                  </div>
                  <div className="text-[20px] font-extrabold leading-none tabular-nums">
                    $38K
                  </div>
                </div>
              </motion.div>

              {/* Real lift: 38 → 95 */}
              <motion.div
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: `${((95 - 38) / 200) * 100}%` }}
                transition={{ delay: 1.4, duration: 0.9, ease: "easeOut" }}
                className="absolute top-0 h-full bg-blue-200 flex items-center justify-center border-l-2 border-dashed border-blue-500"
                style={{ left: `${(38 / 200) * 100}%` }}
              >
                <div className="text-center text-blue-900 px-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.12em]">
                    +$57K lift
                  </div>
                  <div className="text-[14px] font-extrabold leading-none">
                    Real opportunity
                  </div>
                </div>
              </motion.div>

              {/* Scale gridlines */}
              {[50, 100, 150].map((v) => (
                <div
                  key={v}
                  className="absolute top-0 h-full w-px bg-slate-300/60 pointer-events-none"
                  style={{ left: `${(v / 200) * 100}%` }}
                />
              ))}
            </div>

            {/* Markers below bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.1, duration: 0.4 }}
              className="relative mt-2 h-[40px]"
            >
              {/* New take-home: $95K */}
              <div
                className="absolute -translate-x-1/2 flex flex-col items-center"
                style={{ left: `${(95 / 200) * 100}%` }}
              >
                <div className="w-px h-2 bg-blue-500" />
                <div className="text-[12px] font-bold text-blue-700 tabular-nums whitespace-nowrap">
                  New take-home: $95K
                </div>
              </div>
              {/* $100K produced today */}
              <div
                className="absolute -translate-x-1/2 flex flex-col items-center"
                style={{ left: `${(100 / 200) * 100}%`, top: "22px" }}
              >
                <div className="text-[11px] font-medium text-slate-500 tabular-nums whitespace-nowrap">
                  $100K produced today
                </div>
              </div>
              {/* $190K produced */}
              <div
                className="absolute -translate-x-1/2 flex flex-col items-center"
                style={{ left: `${(190 / 200) * 100}%` }}
              >
                <div className="w-px h-2 bg-slate-400" />
                <div className="text-[11px] font-bold text-slate-700 tabular-nums whitespace-nowrap">
                  $190K produced
                </div>
              </div>
            </motion.div>
          </div>

        </motion.div>

        {/* 3 levers — progressive disclosure */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[12.5px] font-bold uppercase tracking-[0.15em] text-slate-500">
              You really have 3 levers to pull — not 2
            </div>
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-600 hover:text-slate-900 transition-colors"
            >
              {expanded ? "Hide details" : "Show details"}
              <ChevronDown
                className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
            {realLevers.map((lever, i) => (
              <motion.div
                key={lever.id}
                layout
                className="bg-white rounded-xl border border-slate-200 shadow-[0_12px_32px_-16px_rgba(15,23,42,0.1)] overflow-hidden border-t-[3px] flex flex-col"
                style={{ borderTopColor: lever.color }}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span
                      className="text-[10px] font-bold uppercase tracking-[0.18em] tabular-nums"
                      style={{ color: lever.color }}
                    >
                      0{i + 1}
                    </span>
                    <span
                      className="text-[12px] font-extrabold tabular-nums"
                      style={{ color: lever.color }}
                    >
                      {lever.pct}
                    </span>
                  </div>
                  <h3 className="text-[15px] font-bold text-slate-900 leading-tight">
                    {lever.label}
                  </h3>
                  <p className="text-[12px] text-slate-500 leading-snug mt-0.5">
                    {lever.hook}
                  </p>
                </div>
                <AnimatePresence initial={false}>
                  {expanded && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-3 flex flex-col gap-2.5 border-t border-slate-100">
                        {lever.bullets.map((b, j) => (
                          <div key={j}>
                            <h4
                              className="text-[12.5px] font-bold leading-tight mb-0.5"
                              style={{ color: lever.color }}
                            >
                              {b.title}
                            </h4>
                            <p className="text-[11.5px] text-slate-600 leading-[1.5]">
                              {b.text}
                            </p>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RealOpportunitySlide;
